/**
 * PersonaList - Manage persona selection and percentage allocation for wizard
 */

import { useState } from 'react';
import { Plus, Trash2, AlertTriangle, Sparkles, Loader2, Edit, Copy, MessageSquare, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { usePersonaTemplates } from '@/hooks/usePersonaTemplates';
import type { WizardPersona } from '@/stores/wizardStore';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';

const PERSONA_COLORS = [
  'hsl(220, 70%, 50%)',
  'hsl(340, 75%, 55%)',
  'hsl(160, 70%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(270, 65%, 55%)',
  'hsl(120, 60%, 45%)',
  'hsl(15, 75%, 50%)',
  'hsl(200, 70%, 50%)',
];

type PersonaListProps = {
  personas: WizardPersona[];
  onAdd: (persona: WizardPersona) => void;
  onUpdate: (id: string, updates: Partial<WizardPersona>) => void;
  onRemove: (id: string) => void;
  onSetPersonas: (personas: WizardPersona[]) => void;
};

export function PersonaList({ 
  personas, 
  onAdd, 
  onUpdate, 
  onRemove,
  onSetPersonas,
}: PersonaListProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('casual-qa');
  const [customName, setCustomName] = useState('');
  const [customInTok, setCustomInTok] = useState(500);
  const [customOutTok, setCustomOutTok] = useState(300);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editingPersona, setEditingPersona] = useState<WizardPersona | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: profiles, isLoading: profilesLoading } = usePersonaProfiles();
  const { data: templates, isLoading: templatesLoading } = usePersonaTemplates();

  // Personas now use simple percentage allocation instead of daily active users
  const totalPct = personas.reduce((sum, p) => {
    const pct = (p as any).pct ?? 0; // pct may be added dynamically
    return sum + pct;
  }, 0);
  const isValid = Math.abs(totalPct - 100) < 0.1;
  const isOver = totalPct > 100;

  const handleLoadTemplate = (templateId: string) => {
    const template = templates?.find(t => t.id === templateId);
    if (!template) return;

    console.log('handleLoadTemplate called:', templateId, template);

    // Mark this template as selected
    setSelectedTemplateId(templateId);

    // Build new personas array
    const newPersonas: WizardPersona[] = [];
    
    template.items?.forEach((item, index) => {
      const profile = profiles?.find(p => p.id === item.persona_profile_id);
      if (!profile) {
        console.warn(`Profile not found: ${item.persona_profile_id}`);
        return;
      }

      const newPersona: WizardPersona & { pct: number } = {
        id: `${profile.id}-${Date.now()}-${index}`,
        name: profile.name,
        description: profile.description || profile.name,
        role: profile.role || profile.name,
        turnsPerSession: item.sessions_per_user_per_day || 3, // Map old sessions to turns
        expertiseType: 'general',
        inTok: profile.prompt_tokens_per_turn,
        outTok: profile.response_tokens_per_turn,
        color: PERSONA_COLORS[index % PERSONA_COLORS.length],
        pct: item.percentage, // Store percentage directly
      };
      
      newPersonas.push(newPersona);
    });

    console.log('Built newPersonas array:', newPersonas);
    console.log('Calling onSetPersonas with', newPersonas.length, 'personas');

    // Replace all personas at once (atomic operation)
    onSetPersonas(newPersonas);
    
    toast.success(`Loaded ${template.name} with ${newPersonas.length} personas`);
  };

  const handleAddPersona = () => {
    const profile = profiles?.find((p) => p.id === selectedProfileId);
    const nextColor = PERSONA_COLORS[personas.length % PERSONA_COLORS.length];
    
    console.log('handleAddPersona called, profile:', profile);
    
    if (profile) {
      const newPersona: WizardPersona & { pct: number } = {
        id: `${profile.id}-${Date.now()}`,
        name: profile.name,
        description: profile.description || profile.name,
        role: profile.role || profile.name,
        turnsPerSession: 3,
        expertiseType: 'general',
        inTok: profile.prompt_tokens_per_turn,
        outTok: profile.response_tokens_per_turn,
        color: nextColor,
        pct: 10,
      };
      console.log('Calling onAdd with persona:', newPersona);
      onAdd(newPersona);
      toast.success(`Added ${profile.name}`);
    } else {
      const newPersona: WizardPersona & { pct: number } = {
        id: `custom-${Date.now()}`,
        name: customName || 'Custom User',
        description: 'Custom user persona',
        role: customName || 'Custom User',
        turnsPerSession: 3,
        expertiseType: 'general',
        inTok: customInTok,
        outTok: customOutTok,
        color: nextColor,
        pct: 10,
      };
      console.log('Calling onAdd with custom persona:', newPersona);
      onAdd(newPersona);
      toast.success('Added custom persona');
    }
    
    setDialogOpen(false);
    setSelectedProfileId('casual-qa');
    setCustomName('');
  };

  const handleUpdatePct = (id: string, newPct: number) => {
    // Store percentage directly on persona
    onUpdate(id, { pct: newPct } as any);
  };

  const handleClearAll = () => {
    if (window.confirm('Remove all personas? You can load a template or add personas manually.')) {
      onSetPersonas([]);
      setSelectedTemplateId(null);
      toast.info('All personas cleared');
    }
  };

  const handleEditPersona = (persona: WizardPersona) => {
    setEditingPersona(persona);
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = (updates: Partial<WizardPersona>) => {
    if (editingPersona) {
      onUpdate(editingPersona.id, updates);
      setIsEditDialogOpen(false);
      setEditingPersona(null);
      toast.success('Persona updated');
    }
  };

  const handleDuplicatePersona = (persona: WizardPersona) => {
    const duplicate: WizardPersona & { pct: number } = {
      ...persona,
      id: `${persona.id}-copy-${Date.now()}`,
      name: `${persona.name} (Copy)`,
      pct: 0,
    } as any;
    onAdd(duplicate);
    toast.success('Persona duplicated');
  };

  const getIconComponent = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    return Icon || LucideIcons.Users;
  };

  // Helper to format expertise type for display
  const formatExpertiseType = (type: string): string => {
    const labels: Record<string, string> = {
      'coding': 'Code Generation',
      'thinking': 'Deep Reasoning',
      'long-conversation': 'Extended Dialog',
      'summarization': 'Summarization',
      'general': 'General Assistant'
    };
    return labels[type] || type;
  };

  if (profilesLoading || templatesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Start Templates */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h4 className="font-semibold text-blue-900 dark:text-blue-100">Quick Start Templates</h4>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
          Load pre-configured persona mixes for common use cases
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates?.map((template) => {
            const Icon = getIconComponent(template.icon);
            return (
              <Button
                key={template.id}
                variant="outline"
                className={`h-auto p-4 flex flex-col items-start gap-2 transition-all ${
                  selectedTemplateId === template.id
                    ? 'bg-blue-100 dark:bg-blue-950/60 border-blue-500 dark:border-blue-400 ring-2 ring-blue-400 dark:ring-blue-500'
                    : 'hover:bg-blue-100 hover:border-blue-400 dark:hover:bg-blue-950/40'
                }`}
                onClick={() => handleLoadTemplate(template.id)}
              >
                <div className="flex items-center gap-2 w-full">
                  <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-sm">{template.name}</span>
                  {selectedTemplateId === template.id && (
                    <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground text-left line-clamp-2">
                  {template.description}
                </span>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  {template.items?.length} personas • {template.total_users.toLocaleString()} users
                </span>
              </Button>
            );
          })}
        </div>
      </Card>

      {/* Clear All Button */}
      {personas.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All Personas
          </Button>
        </div>
      )}

      {/* Persona Cards */}
      {personas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {personas.map((persona) => {
            const pct = (persona as any).pct ?? 0;

            return (
              <Card 
                key={persona.id} 
                className="relative overflow-hidden border-l-4" 
                style={{ borderLeftColor: persona.color }}
              >
                <div className="p-5">
                  {/* Header with name, role, and action buttons */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-lg truncate">{persona.name}</h4>
                      <p className="text-sm text-muted-foreground truncate">{persona.role}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditPersona(persona)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDuplicatePersona(persona)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onRemove(persona.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {persona.description}
                  </p>

                  {/* Badges Grid */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="text-xs">
                      <MessageSquare className="h-3 w-3 mr-1" />
                      {persona.turnsPerSession} turns/session
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <Brain className="h-3 w-3 mr-1" />
                      {formatExpertiseType(persona.expertiseType)}
                    </Badge>
                  </div>

                  {/* Token Information */}
                  <div className="text-sm text-muted-foreground mb-4">
                    Input: {persona.inTok.toLocaleString()} tokens • 
                    Output: {persona.outTok.toLocaleString()} tokens
                  </div>

                  {/* Percentage Controls */}
                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Usage Percentage</span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={pct}
                          onChange={(e) => {
                            const newPct = Math.max(0, Math.min(100, Number(e.target.value)));
                            handleUpdatePct(persona.id, newPct);
                          }}
                          className="w-16 text-right h-8"
                        />
                        <span className="text-sm">%</span>
                      </div>
                    </div>
                    <Slider
                      value={[pct]}
                      onValueChange={([val]) => handleUpdatePct(persona.id, val)}
                      max={100}
                      step={1}
                    />
                    <p className="text-xs text-muted-foreground">
                      Percentage of total concurrent users assigned to this persona
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Subtle validation indicator */}
      {personas.length > 0 && (
        <div className="text-right">
          <span className={`text-sm ${
            isValid 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-muted-foreground'
          }`}>
            Total: {totalPct.toFixed(1)}%
            {isValid && ' ✓'}
          </span>
        </div>
      )}

      {/* Add Persona Button */}
      <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Persona
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Persona</DialogTitle>
            <DialogDescription>
              Select a pre-defined persona profile or create a custom one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Persona Type</label>
              <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profiles?.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom Persona</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedProfileId === 'custom' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">Persona Name</label>
                  <Input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="e.g., Content Creator"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Input Tokens</label>
                    <Input
                      type="number"
                      value={customInTok}
                      onChange={(e) => setCustomInTok(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Output Tokens</label>
                    <Input
                      type="number"
                      value={customOutTok}
                      onChange={(e) => setCustomOutTok(Number(e.target.value))}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPersona}>
              Add Persona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Persona Dialog */}
      {editingPersona && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Persona</DialogTitle>
              <DialogDescription>
                Modify the persona details and characteristics
              </DialogDescription>
            </DialogHeader>
            <PersonaEditForm
              persona={editingPersona}
              onSave={handleSaveEdit}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setEditingPersona(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PersonaEditForm({ 
  persona, 
  onSave, 
  onCancel 
}: { 
  persona: WizardPersona; 
  onSave: (updates: Partial<WizardPersona>) => void; 
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: persona.name,
    role: persona.role,
    description: persona.description,
    turnsPerSession: persona.turnsPerSession,
    expertiseType: persona.expertiseType,
    inTok: persona.inTok,
    outTok: persona.outTok,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-name">Name</Label>
          <Input
            id="edit-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-role">Role</Label>
          <Input
            id="edit-role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-description">Description</Label>
        <Textarea
          id="edit-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Turns per session */}
        <div className="space-y-2">
          <Label htmlFor="edit-turns">Turns per Session</Label>
          <Input
            id="edit-turns"
            type="number"
            min="1"
            max="50"
            value={formData.turnsPerSession}
            onChange={(e) => setFormData({ ...formData, turnsPerSession: Number(e.target.value) })}
            required
          />
          <p className="text-xs text-muted-foreground">
            Number of back-and-forth exchanges in a typical conversation
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Expertise Type</Label>
        <RadioGroup
          value={formData.expertiseType}
          onValueChange={(value) => setFormData({ ...formData, expertiseType: value as any })}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="coding" id="edit-expertise-coding" />
            <Label htmlFor="edit-expertise-coding" className="font-normal">
              Code Generation - Writing, debugging, and explaining code
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="thinking" id="edit-expertise-thinking" />
            <Label htmlFor="edit-expertise-thinking" className="font-normal">
              Deep Reasoning - Complex problem solving and analysis
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="long-conversation" id="edit-expertise-long" />
            <Label htmlFor="edit-expertise-long" className="font-normal">
              Extended Dialog - Multi-turn conversations with context
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="summarization" id="edit-expertise-summary" />
            <Label htmlFor="edit-expertise-summary" className="font-normal">
              Summarization - Condensing and extracting key information
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="general" id="edit-expertise-general" />
            <Label htmlFor="edit-expertise-general" className="font-normal">
              General Assistant - Broad range of everyday tasks
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-inTok">Input Tokens</Label>
          <Input
            id="edit-inTok"
            type="number"
            min="100"
            max="100000"
            step="100"
            value={formData.inTok}
            onChange={(e) => setFormData({ ...formData, inTok: Number(e.target.value) })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-outTok">Output Tokens</Label>
          <Input
            id="edit-outTok"
            type="number"
            min="50"
            max="50000"
            step="50"
            value={formData.outTok}
            onChange={(e) => setFormData({ ...formData, outTok: Number(e.target.value) })}
            required
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Save Changes
        </Button>
      </div>
    </form>
  );
}
